import { FC } from 'react';

import Map from '@shared/components/Map';
import { Coord } from '@shared/interfaces/map.interface';

import {
  formatCoordMove,
  hasCoordValue,
  isAddressChanged,
} from './funeralHomeAddressChange.util';

/** 좌표가 있으면 지도, 없으면 점선 칸. className으로 높이·모서리만 바꾼다(기본 200px). */
export const AddressMap: FC<{
  coord?: Coord | null;
  name: string;
  emptyText: string;
  className?: string;
}> = ({ coord, name, emptyText, className = 'h-[200px]' }) =>
  hasCoordValue(coord) ? (
    <div className={`w-full ${className}`} key={`${coord.x},${coord.y}`}>
      <Map coord={coord} name={name} />
    </div>
  ) : (
    //* 지도 서버 장애(MapErrorFallback)와 구분되도록 점선 칸으로 그린다
    <p
      className={`center-box w-full break-keep border border-dashed border-gray-300 px-4 text-center text-xs font-medium text-gray-500 ${className}`}
    >
      {emptyText}
    </p>
  );

interface IProps {
  name: string;
  savedAddress?: string;
  savedCoord?: Coord | null;
  //* 주소검색으로 새로 고른 주소(고르기 전엔 null)
  selected: { address: string; coord?: Coord } | null;
  //* 고른 결과가 저장값과 같아 비교할 내용이 없을 때 그 이유를 알린다
  showNoChangeNotice?: boolean;
  //* 고른 주소의 좌표 대신 기존 좌표로 저장
  keepSavedCoord?: boolean;
  onKeepSavedCoordChange?: (keep: boolean) => void;
  onReset: () => void;
}

export const FuneralHomeAddressPreview: FC<IProps> = ({
  name,
  savedAddress,
  savedCoord,
  selected,
  showNoChangeNotice = false,
  keepSavedCoord = false,
  onKeepSavedCoordChange,
  onReset,
}: IProps) => {
  const targetCoord = keepSavedCoord ? savedCoord : selected?.coord;

  if (!selected || !isAddressChanged(savedAddress, savedCoord, selected, keepSavedCoord)) {
    return (
      <div className="space-y-2">
        <h5 className="font-medium">현재주소</h5>
        <p className="pl-2">{savedAddress || '주소 없음'}</p>
        {showNoChangeNotice && (
          <div className="flex items-center justify-between gap-2 pl-2">
            <p role="status" className="break-keep text-xs font-medium theme-text-main">
              고른 결과가 현재 저장된 주소·좌표와 같아 변경 전/후를 표시하지 않습니다.
            </p>
            <button
              type="button"
              className="shrink-0 border px-2 py-1 text-xs font-medium theme-bg-1 theme-border-1"
              onClick={onReset}
            >
              되돌리기
            </button>
          </div>
        )}
        <AddressMap
          coord={savedCoord}
          name={name}
          emptyText="저장된 좌표 없음 · 아래 주소검색으로 지정"
        />
      </div>
    );
  }

  const moveText =
    keepSavedCoord && hasCoordValue(savedCoord)
      ? '기존 좌표 유지'
      : formatCoordMove(savedCoord, selected.coord);
  //* 기존·새 좌표가 둘 다 있을 때만 고를 게 있다
  const canKeepSavedCoord = hasCoordValue(savedCoord) && hasCoordValue(selected.coord);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h5 className="font-medium">주소 변경</h5>
          <span className="text-xs font-medium theme-text-main">저장 전</span>
          <span className="text-xs theme-text-8">{moveText}</span>
        </div>
        <button
          type="button"
          className="shrink-0 border px-2 py-1 text-xs font-medium theme-bg-1 theme-border-1"
          onClick={onReset}
        >
          되돌리기
        </button>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <section className="flex flex-col gap-2 border p-2 theme-bg-1 theme-border-1">
          <span className="self-start bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600">
            변경 전
          </span>
          <p className="break-keep theme-text-8">{savedAddress || '주소 없음'}</p>
          {/* 주소 길이가 달라도 두 지도 높이를 맞춘다 */}
          <div className="mt-auto">
            <AddressMap coord={savedCoord} name={name} emptyText="저장된 좌표 없음" />
          </div>
        </section>
        <section className="flex flex-col gap-2 border p-2 theme-bg-1 theme-border-main">
          <span className="self-start px-2 py-0.5 text-xs font-medium text-white theme-bg-main">
            변경 후
          </span>
          <p className="break-keep font-bold">{selected.address}</p>
          {canKeepSavedCoord && onKeepSavedCoordChange && (
            <label className="flex items-center gap-1.5 text-xs font-medium">
              <input
                type="checkbox"
                className="checkbox"
                checked={keepSavedCoord}
                onChange={(event) => onKeepSavedCoordChange(event.currentTarget.checked)}
              />
              기존 좌표 유지 (주소만 변경)
            </label>
          )}
          <div className="mt-auto">
            {/* 새 좌표가 없으면 기존 좌표 지도를 대신 그리지 않는다(새 위치로 오해) */}
            <AddressMap coord={targetCoord} name={name} emptyText="새 좌표 없음" />
          </div>
        </section>
      </div>
    </div>
  );
};
